```
<script setup lang="ts">
import { h, ref } from 'vue'
import { type FormInst, type FormRules, NButton, NForm, NFormItem, useDialog, useMessage } from 'naive-ui'
import { t } from '@/locales'
import { joinClassroom } from '@/api/classroom'

defineProps<{
  show: boolean
}>()

const emit = defineEmits(['on-close'])

const message = useMessage()
const dialog = useDialog()

const formRef = ref<FormInst | null>(null)
const classLink = ref<string>('')
const loading = ref<boolean>(false)

const rulesRef: FormRules = {
  classLink: [
    {
      required: true,
      message: t('validation.required', { name: t('auth.classLink') }),
      trigger: ['blur', 'input', 'change'],
    },
  ],
}

const handleClose = () => {
  emit('on-close', false)
  classLink.value = ''
}

const handleCancel = () => {
  dialog.destroyAll()
  emit('on-close', true)
}

const handleJoinClassSuccess = () => {
  dialog.warning({
    title: t('auth.joinClassSuccess'),
    showIcon: false,
    autoFocus: false,
    maskClosable: false,
    closable: false,
    class: '!rounded-3xl !w-[475px]',
    content: () =>
      h('div', { }, [
        h('div', {}, t('auth.joinClassSuccessSent')),
        h('div', {}, t('auth.joinClassSuccessPendingApproval')),
      ],
      ),
    action: () =>
      h(
        'div',
        { class: 'flex item-center justify-center gap-3 w-full' },
        [
          h(NButton, { type: 'primary', class: 'max-w-[180px] !w-full', onClick: () => handleCancel() }, t('common.done')),
        ],
      ),
  })
}

const handleJoinClass = () => {
  formRef.value?.validate().then(async () => {
    try {
      loading.value = true
      const params = {
        link: classLink.value,
      }
      const { data } = await joinClassroom(params)
      if (data?.success)
        handleJoinClassSuccess()

      else
        message.error(t('auth.joinClassFail'))
    }
    catch (error: any) {
      message.error(error?.response?.data?.message)
    }
    finally {
      loading.value = false
    }
  }).catch(() => {})
}
</script>

<template>
  <AppModal
    :show="show"
    preset="card"
    transform-origin="center"
    style="width: 550px"
    class="!rounded-3xl"
    content-class="!px-6 !py-2"
    header-class="!p-6 text-center"
    footer-class="!p-6 !pt-0 !w-full"
    :mask-closable="true"
    @after-leave="handleClose"
    @esc="handleClose"
  >
    <template #header>
      <div class="text-xl font-bold">
        {{ $t('auth.joinClass') }}
      </div>
    </template>

    <NForm ref="formRef" :model="{ classLink }" :rules="rulesRef">
      <NFormItem :label="$t('auth.classLink')" path="classLink">
        <AppInput
          v-model:value="classLink"
          placeholder=""
          size="medium"
          class="!rounded-2xl"
        />
      </NFormItem>
    </NForm>

    <template #footer>
      <div class="flex items-center justify-center gap-4">
        <AppButton
          ghost
          size="medium"
          class="min-w-[180px]"
          type="primary"
          @click="handleClose"
        >
          {{ $t('common.cancel') || 'Hủy' }}
        </AppButton>
        <AppButton
          size="medium"
          class="min-w-[180px]"
          type="primary"
          :disabled="loading"
          :loading="loading"
          @click="handleJoinClass"
        >
          {{ $t('auth.joinClassButton') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>
